import { useEffect, useRef } from "react";

import { cn } from "@repo/blacksmith-ui/cn";

const TRAIL_INK_ON_LIGHT = "#1c1c1c";
const TRAIL_INK_ON_DARK = "#ffffff";
const TRAIL_PIXEL = 1;
const TRAIL_GAP = 5;
const TRAIL_CELL = TRAIL_PIXEL + TRAIL_GAP;
const TRAIL_SPLAT_RADIUS_CELLS = 20;
// Exponential splat e^(-d/s): sharply peaked under the cursor (unlike a
// gaussian's flat top) and decaying radially to nothing. The scale puts the
// tail below visibility before the sampling cutoff, so no disc boundary is
// ever visible.
const TRAIL_SPLAT_FALLOFF_CELLS = TRAIL_SPLAT_RADIUS_CELLS / 4.6;
const TRAIL_SPLAT_AMPLITUDE = 0.345;
// Intensity accumulates past 1 (bounded by FIELD_MAX) and maps to alpha via
// the soft knee 1 - e^(-k·v): darkness approaches full asymptotically, so the
// center gets dark without ever clipping into a flat-topped disc.
const TRAIL_FIELD_MAX = 4;
const TRAIL_ALPHA_KNEE = 0.8;
const TRAIL_DECAY_PER_FRAME = 0.93;

/**
 * Liquidy mouse trail quantized to a pixel grid: pointer movement splats
 * radially decaying blobs into a per-cell intensity field, each frame decays
 * the field and redraws it as small square dots. Renders into the positioned
 * parent.
 *
 * `surface` names the backdrop the dots land on, which is not always the
 * theme: the yellow hero tile stays light in dark mode, so it pins "light".
 */
export function PixelTrailCanvas({
  surface = "auto",
  className,
}: {
  surface?: "auto" | "light" | "dark";
  className?: string;
} = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const surfaceRef = useRef(surface);
  useEffect(() => {
    surfaceRef.current = surface;
  }, [surface]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !parent || !ctx) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let running = false;
    let cols = 0;
    let rows = 0;
    let dpr = 1;
    let field = new Float32Array(0);
    let noise = new Float32Array(0);
    let hasLastPoint = false;
    let lastX = 0;
    let lastY = 0;
    // One pixel per cell, holding that cell's ink and alpha. Each frame it is
    // scaled up onto the trail canvas and cut down to the dots by `dots`, so
    // the GPU sees three draws instead of one fillRect per live cell.
    const cells = document.createElement("canvas");
    const cellsCtx = cells.getContext("2d");
    if (!cellsCtx) return;
    let cellsImage = cellsCtx.createImageData(1, 1);
    let cellsInk = "";
    // Every dot at full alpha, drawn with the same fillRect calls as the ink
    // so antialiasing at fractional device pixel ratios is unchanged.
    const dots = document.createElement("canvas");
    let dotsStale = true;
    const drawn = { x0: 0, y0: 0, x1: -1, y1: -1 };

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(rect.width / TRAIL_CELL);
      rows = Math.ceil(rect.height / TRAIL_CELL);
      field = new Float32Array(cols * rows);
      noise = new Float32Array(cols * rows);
      for (let i = 0; i < noise.length; i++) {
        const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
        noise[i] = s - Math.floor(s);
      }
      cells.width = Math.max(1, cols);
      cells.height = Math.max(1, rows);
      cellsImage = cellsCtx.createImageData(cells.width, cells.height);
      cellsInk = "";
      dotsStale = true;
      drawn.x1 = -1;
    };
    resize();
    // An idle trail is fully transparent, so its backing size can wait for
    // the next pointer move. The band resizes every frame while it animates,
    // and reallocating the canvas each time costs main-thread time and GC.
    let sizeStale = false;
    const resizeObserver = new ResizeObserver(() => {
      if (running) resize();
      else sizeStale = true;
    });
    resizeObserver.observe(parent);

    const paintDots = () => {
      dots.width = canvas.width;
      dots.height = canvas.height;
      const dotsCtx = dots.getContext("2d");
      if (!dotsCtx) return;
      dotsCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          dotsCtx.fillRect(
            x * TRAIL_CELL,
            y * TRAIL_CELL,
            TRAIL_PIXEL,
            TRAIL_PIXEL,
          );
        }
      }
      dotsStale = false;
    };

    const setCellsInk = (ink: string) => {
      const r = parseInt(ink.slice(1, 3), 16);
      const g = parseInt(ink.slice(3, 5), 16);
      const b = parseInt(ink.slice(5, 7), 16);
      const data = cellsImage.data;
      for (let o = 0; o < data.length; o += 4) {
        data[o] = r;
        data[o + 1] = g;
        data[o + 2] = b;
      }
      cellsInk = ink;
    };

    const splat = (xCss: number, yCss: number) => {
      const cx = xCss / TRAIL_CELL;
      const cy = yCss / TRAIL_CELL;
      const r = TRAIL_SPLAT_RADIUS_CELLS;
      const r2 = r * r;
      const x0 = Math.max(0, Math.floor(cx - r));
      const x1 = Math.min(cols - 1, Math.ceil(cx + r));
      const y0 = Math.max(0, Math.floor(cy - r));
      const y1 = Math.min(rows - 1, Math.ceil(cy + r));
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const dx = x - cx;
          const dy = y - cy;
          const d2 = dx * dx + dy * dy;
          if (d2 > r2) continue;
          const i = y * cols + x;
          // Mild per-cell grain keeps the dither organic without breaking
          // the radial gradient.
          const grain = 0.7 + 0.3 * noise[i];
          const falloff = Math.exp(-Math.sqrt(d2) / TRAIL_SPLAT_FALLOFF_CELLS);
          field[i] = Math.min(
            TRAIL_FIELD_MAX,
            field[i] + TRAIL_SPLAT_AMPLITUDE * grain * falloff,
          );
        }
      }
    };

    const step = () => {
      const onDark =
        surfaceRef.current === "auto"
          ? !!canvas.closest(".dark")
          : surfaceRef.current === "dark";
      const ink = onDark ? TRAIL_INK_ON_DARK : TRAIL_INK_ON_LIGHT;
      if (ink !== cellsInk) setCellsInk(ink);
      const data = cellsImage.data;
      let x0 = cols;
      let y0 = rows;
      let x1 = -1;
      let y1 = -1;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = y * cols + x;
          const v = field[i];
          if (v < 0.02) {
            field[i] = 0;
            data[i * 4 + 3] = 0;
            continue;
          }
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
          field[i] = v * TRAIL_DECAY_PER_FRAME;
          data[i * 4 + 3] = 255 * (1 - Math.exp(-TRAIL_ALPHA_KNEE * v));
        }
      }
      const alive = x1 >= 0;

      // Repaint the union of this frame's live cells and last frame's, a cell
      // of margin each side for the antialiased dot edges.
      const unionX0 = Math.min(x0, drawn.x1 >= 0 ? drawn.x0 : cols) - 1;
      const unionY0 = Math.min(y0, drawn.x1 >= 0 ? drawn.y0 : rows) - 1;
      const unionX1 = Math.max(x1, drawn.x1) + 1;
      const unionY1 = Math.max(y1, drawn.y1) + 1;
      if (unionX1 >= unionX0 && (alive || drawn.x1 >= 0)) {
        if (alive && dotsStale) paintDots();
        ctx.save();
        ctx.beginPath();
        ctx.rect(
          unionX0 * TRAIL_CELL,
          unionY0 * TRAIL_CELL,
          (unionX1 - unionX0 + 1) * TRAIL_CELL,
          (unionY1 - unionY0 + 1) * TRAIL_CELL,
        );
        ctx.clip();
        if (alive) {
          cellsCtx.putImageData(cellsImage, 0, 0);
          ctx.imageSmoothingEnabled = false;
          ctx.globalCompositeOperation = "copy";
          // Offset half a cell so every dot, antialiased edge included, sits
          // well inside its cell's scaled-up pixel whatever the pixel ratio.
          ctx.drawImage(
            cells,
            -TRAIL_CELL / 2 + TRAIL_PIXEL / 2,
            -TRAIL_CELL / 2 + TRAIL_PIXEL / 2,
            cells.width * TRAIL_CELL,
            cells.height * TRAIL_CELL,
          );
          ctx.globalCompositeOperation = "destination-in";
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.drawImage(dots, 0, 0);
        } else {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        ctx.restore();
      }
      drawn.x0 = x0;
      drawn.y0 = y0;
      drawn.x1 = x1;
      drawn.y1 = y1;

      if (alive) {
        raf = requestAnimationFrame(step);
      } else {
        running = false;
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (sizeStale) {
        sizeStale = false;
        resize();
      }
      const rect = parent.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      // Splat along the segment from the last event so fast swipes leave a
      // continuous ribbon instead of disconnected blobs.
      if (hasLastPoint) {
        const dx = x - lastX;
        const dy = y - lastY;
        const dist = Math.hypot(dx, dy);
        const steps = Math.min(24, Math.floor(dist / (TRAIL_CELL * 2)));
        for (let s = 1; s <= steps; s++) {
          splat(lastX + (dx * s) / (steps + 1), lastY + (dy * s) / (steps + 1));
        }
      }
      splat(x, y);
      hasLastPoint = true;
      lastX = x;
      lastY = y;
      if (!running) {
        running = true;
        raf = requestAnimationFrame(step);
      }
    };
    const onPointerLeave = () => {
      hasLastPoint = false;
    };

    parent.addEventListener("pointermove", onPointerMove);
    parent.addEventListener("pointerleave", onPointerLeave);
    return () => {
      parent.removeEventListener("pointermove", onPointerMove);
      parent.removeEventListener("pointerleave", onPointerLeave);
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      // Rounded to match its host tile so the trail respects the corners even
      // when the host does not clip overflow.
      className={cn(
        "pointer-events-none absolute inset-0 size-full rounded-lg",
        className,
      )}
    />
  );
}
